import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  format,
  isBefore,
  isSameDay,
  startOfDay,
  endOfDay,
  isAfter,
  addMonths,
} from "date-fns";
import { enUS, pl } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import orangeSortIcon from "./../../media/orange-sort-icon.svg";
import deleteButtonIcon from "./../../media/delete-button.svg";
import sortIcon from "./../../media/sort-icon.svg";
import { useAuth, API_URL } from "./../../AuthContext.js";
import Logger from "../../utils/Logger";
import { useNotification } from "../../components/NotificationContext";
import { createPortal } from "react-dom";
import eventBus from "../../components/eventBus.js";
import httpClient from "../../services/httpClient.js";
import JSZip from "jszip";
import "../../styleSheet/main/reservations/roomsReservations/roomsReservations.scss";
import { FontSize } from "@tiptap/extension-text-style";
import { useTranslation } from "react-i18next";

const RoomsReservations = () => {
  const { t, i18n } = useTranslation();
  const datePickerLocale = i18n.language === "en" ? enUS : pl;
  const [date, setDate] = useState(null);
  const [startHour, setStartHour] = useState(null);
  const [endHour, setEndHour] = useState(null);
  const [tripPurpose, setTripPurpose] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [errors, setErrors] = useState({});
  const [hoveredButton, setHoveredButton] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(false);
  const [isDatesSet, setIsDatesSet] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [newParticipantId, setNewParticipantId] = useState(""); // Store selected participant ID
  const [employees, setEmployees] = useState([]); // Store list of employees from API
  const [confirmationData, setConfirmationData] = useState({});
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Loading state for reservation creation
  // const { showNotification } = useNotification();

  const navigate = useNavigate();

  const { userData } = useAuth();
  const getEmail = () => userData.userEmail;

  useEffect(() => {
    const fetchRooms = async () => {
      if (!date || !startHour || !endHour) {
        Logger.error("Nie można pobrać sal: Brak wymaganych wartości");
        return;
      }

      try {
        // Format as full datetime strings (yyyy-MM-ddTHH:mm:ss)
        const formattedStartTime = `${format(date, "yyyy-MM-dd")}T${format(
          startHour,
          "HH:mm:ss",
        )}`;
        const formattedEndTime = `${format(date, "yyyy-MM-dd")}T${format(
          endHour,
          "HH:mm:ss",
        )}`;

        const formattedStartTimeUrl = encodeURIComponent(formattedStartTime);
        const formattedEndTimeUrl = encodeURIComponent(formattedEndTime);

        const response = await httpClient.get(
          `/rooms/available-rooms?StartTime=${formattedStartTimeUrl}&EndTime=${formattedEndTimeUrl}`,
          { withAuth: true },
        );

        const data = await response.data;
        // Logger.log("Dane z API:", data);

        if (Array.isArray(data)) {
          setRooms(data);
          setIsDatesSet(true);
          Logger.log(rooms);
          Logger.log(isDatesSet);
        } else {
          Logger.error("Błąd: API nie zwróciło tablicy!", data);
        }
      } catch (error) {
        Logger.error("Błąd podczas pobierania sal:", error);
      }
    };

    fetchRooms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, startHour, endHour]);

  useEffect(() => {
    // Fetch employee list from API with availability information
    const fetchEmployees = async () => {
      // Only fetch if we have both date and time ranges set
      if (!date || !startHour || !endHour) {
        setEmployees([]);
        return;
      }

      try {
        // Format as full datetime strings (yyyy-MM-ddTHH:mm:ss)
        const formattedStartTime = `${format(date, "yyyy-MM-dd")}T${format(
          startHour,
          "HH:mm:ss",
        )}`;
        const formattedEndTime = `${format(date, "yyyy-MM-dd")}T${format(
          endHour,
          "HH:mm:ss",
        )}`;

        const formattedStartTimeUrl = encodeURIComponent(formattedStartTime);
        const formattedEndTimeUrl = encodeURIComponent(formattedEndTime);

        const response = await httpClient.get(
          `/Users/available-employees?StartTime=${formattedStartTimeUrl}&EndTime=${formattedEndTimeUrl}`,
          { withAuth: true },
        );
        const data = response.data;
        setEmployees(data);
      } catch (error) {
        Logger.error("Błąd przy pobieraniu uczestników", error);
        setEmployees([]);
      }
    };

    fetchEmployees();
  }, [date, startHour, endHour]);

  const handleCarSelection = (car) => {
    setSelectedCar(selectedCar === car ? null : car);
  };

  const handleDateChange = (date) => {
    setDate(date);
  };

  const handleStartHourChange = (date) => {
    setStartHour(date);
    // Jeśli nowa godzina rozpoczęcia jest >= endHour, wyczyść endHour
    if (date && endHour && !isBefore(date, endHour)) {
      setEndHour(null);
    }
    if (date && endHour) {
      // Sprawdzanie, czy godzina rozpoczęcia jest wcześniejsza niż zakończenia
      if (isSameDay(date, endHour)) {
        if (!isBefore(date, endHour)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            startHour: t("reservations.rooms.errors.invalidTimeRange"),
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, startHour: null }));
        }
      }
    }
  };

  const handleEndHourChange = (dateNow) => {
    setEndHour(dateNow);
  };

  const handleAddParticipant = () => {
    if (newParticipantId) {
      const selectedEmployee = employees.find(
        (emp) => emp.id === newParticipantId,
      );
      if (!selectedEmployee) {
        return;
      }
      if (selectedEmployee.email === getEmail()) {
        // showNotification("Nie możesz dodać siebie jako uczestnika.", "error");
        return; // Nie dodawaj uczestnika, jeśli to użytkownik
      }
      if (selectedEmployee.busy === true) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          participants: t("reservations.rooms.employeeBusy"),
        }));
        // showNotification("Ten pracownik jest zajęty w wybranym terminie.", "error");
        return; // Nie dodawaj zajętego uczestnika
      }
      if (selectedEmployee) {
        setParticipants([
          ...participants,
          {
            name: `${selectedEmployee.firstName} ${selectedEmployee.lastName}`,
            email: selectedEmployee.email,
            id: selectedEmployee.id,
            busy: selectedEmployee.busy,
          },
        ]);
        setErrors((prevErrors) => ({ ...prevErrors, participants: null }));
      }
      setNewParticipantId(""); // Reset the selected participant
    }
  };

  const handleParticipantChange = (participantId) => {
    const selectedEmployee = employees.find((emp) => emp.id === participantId);

    if (selectedEmployee?.busy === true) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        participants: t("reservations.rooms.employeeBusy"),
      }));
      setNewParticipantId("");
      return;
    }

    setErrors((prevErrors) => ({ ...prevErrors, participants: null }));
    setNewParticipantId(participantId);
  };

  const participantOptions = employees
    .filter((employee) => employee.email !== getEmail())
    .filter(
      (employee) =>
        !participants.some((participant) => participant.id === employee.id),
    )
    .sort((a, b) => {
      if (a.busy !== b.busy) {
        return a.busy ? 1 : -1;
      }
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((employee) => ({
      value: employee.id,
      label: `${employee.firstName} ${employee.lastName}${
        employee.busy ? ` (${t("reservations.rooms.busy")})` : ""
      }`,
      isBusy: employee.busy === true,
    }));

  const selectedParticipantOption =
    participantOptions.find((option) => option.value === newParticipantId) ||
    null;

  const participantSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "#000000",
      borderColor: state.isFocused ? "#ff9900" : "#efefef",
      borderRadius: 0,
      minHeight: "46px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#ff9900",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#000000",
      border: "1px solid #efefef",
      borderRadius: 0,
      zIndex: 20,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    input: (provided) => ({
      ...provided,
      color: "#ffffff",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#111111" : "#000000",
      color: "#ffffff",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.65 : 1,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "#efefef",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#ffffff",
      "&:hover": {
        color: "#ffffff",
      },
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#000000",
    }),
  };

  const handleRemoveParticipant = (index) => {
    setParticipants(participants.filter((_, i) => i !== index));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  // Step 1: Modify handleReservation to show confirmation
  const handleReservation = async () => {
    const newErrors = {};

    if (!date) {
      newErrors.date = t("reservations.rooms.errors.selectDate");
    }
    if (!startHour) {
      newErrors.startHour = t("reservations.rooms.errors.selectStartTime");
    }
    if (!endHour) {
      newErrors.endHour = t("reservations.rooms.errors.selectEndTime");
    }

    if (startHour && endHour && isSameDay(startHour, endHour)) {
      if (!isBefore(startHour, endHour)) {
        newErrors.endHour = t("reservations.rooms.errors.invalidTimeRange");
      }
    } else if (startHour && endHour && !isSameDay(startHour, endHour)) {
      newErrors.endHour = t("reservations.rooms.errors.sameDayRequired");
    }

    if (!tripPurpose) {
      newErrors.tripPurpose = t("reservations.rooms.errors.enterPurpose");
    }
    if (!selectedCar) {
      newErrors.selectedCar = t("reservations.rooms.errors.selectRoom");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      // Show confirmation modal
      setConfirmationData({
        car: selectedCar,
        date: format(date, "dd-MM-yyyy"),
        startHour: format(startHour, "HH:mm"),
        endHour: format(endHour, "HH:mm"),
        participants: participants.map((p) => p.name),
        tripPurpose: tripPurpose,
        description: description,
        files: files,
      });

      setShowConfirmation(true);
    }
  };

  // Step 4: Modify handleConfirm to send the POST request
  const handleConfirm = async () => {
    Logger.log("Rezerwacja potwierdzona");
    setIsLoading(true); // Start loading

    try {
      const formData = new FormData();

      // Format datetime as local datetime string (yyyy-MM-ddTHH:mm:ss)
      const startTime =
        date && startHour
          ? `${format(date, "yyyy-MM-dd")}T${format(startHour, "HH:mm:ss")}`
          : "";
      const endTime =
        date && endHour
          ? `${format(date, "yyyy-MM-dd")}T${format(endHour, "HH:mm:ss")}`
          : "";

      // Add form fields with camelCase names
      formData.append(
        "roomId",
        rooms.find((room) => room.name === selectedCar)?.id,
      );
      formData.append("startTime", startTime);
      formData.append("endTime", endTime);
      formData.append("title", tripPurpose);
      formData.append("description", description || "");

      // Add participant IDs
      const participantIds = participants
        .map((participant) => {
          // First try to find by id if available
          let employee;
          if (participant.id) {
            employee = employees.find((emp) => emp.id === participant.id);
          }

          // If not found by id, try by email
          if (!employee && participant.email) {
            employee = employees.find((emp) => emp.email === participant.email);
          }

          // If still not found, try by name match
          if (!employee && participant.name) {
            employee = employees.find(
              (emp) => `${emp.firstName} ${emp.lastName}` === participant.name,
            );
          }

          return employee?.id || participant.id; // Use participant.id as fallback
        })
        .filter((id) => id !== undefined);

      participantIds.forEach((id) => {
        formData.append("participantIds", id);
      });

      // Handle file attachment (zip if multiple files)
      if (files.length > 1) {
        const zip = new JSZip();
        files.forEach((file) => zip.file(file.name, file));
        const zipFileName = `conference-room-${Date.now()}.zip`;
        const zipBlob = await zip.generateAsync({ type: "blob" });
        const zipFile = new File([zipBlob], zipFileName, {
          type: "application/zip",
        });
        formData.append("attachment", zipFile);
      } else if (files.length === 1) {
        formData.append("attachment", files[0]);
      }

      Logger.log("Dane rezerwacji FormData:", {
        roomId: rooms.find((room) => room.name === selectedCar)?.id,
        startTime,
        endTime,
        title: tripPurpose,
        description,
        participantIds,
        filesCount: files.length,
      });

      await httpClient.post("/RoomReservations", formData, {
        withAuth: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // showNotification("Rezerwacja została dodana pomyślnie!", "success");
      setShowConfirmation(false);
      eventBus.emit("room-reserved", {});
      navigate("/rezerwacje/sale-konferencyjne/");
    } catch (error) {
      Logger.error("Błąd podczas wysyłania rezerwacji:", error);
      // showNotification("Wystąpił błąd podczas wysyłania rezerwacji.", "error");
    } finally {
      setIsLoading(false); // Stop loading regardless of success or error
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleCancelChange = () => {
    navigate("/rezerwacje/sale-konferencyjne/");
  };

  const handleIconClick = () => {
    navigate("/rezerwacje/sale-konferencyjne");
  };

  // Dodajemy funkcję do renderowania modalu przez portal
  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    return createPortal(
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt={t("common.close")}
            onClick={handleCancel}
          />
          <div className="areYouSure">
            <p>{t("reservations.rooms.confirmCreate")}</p>
          </div>
          <div>
            <p>
              <strong>{t("reservations.rooms.room")}: </strong>
              {confirmationData.car}
            </p>
            <p>
              <strong>{t("reservations.rooms.date")}:</strong>{" "}
              {confirmationData.date}
            </p>
            <p>
              <strong>{t("reservations.rooms.startTime")}: </strong>
              {confirmationData.startHour}
            </p>
            <p>
              <strong>{t("reservations.rooms.endTime")}: </strong>
              {confirmationData.endHour}
            </p>
            <p>
              <strong>{t("reservations.rooms.participants")}: </strong>
              {confirmationData.participants.join(", ")}
            </p>
            <p>
              <strong>{t("reservations.rooms.meetingPurpose")}: </strong>
              {confirmationData.tripPurpose}
            </p>
            {confirmationData.description && (
              <p>
                <strong>{t("reservations.rooms.description")}: </strong>
                {confirmationData.description}
              </p>
            )}
            {confirmationData.files && confirmationData.files.length > 0 && (
              <div>
                <strong>{t("reservations.rooms.attachments")}: </strong>
                <ul style={{ marginLeft: "20px" }}>
                  {confirmationData.files.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="confirmation-buttons">
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t("reservations.cars.creatingReservation")}</p>
              </div>
            ) : (
              <>
                <button onClick={handleConfirm}>{t("common.yes")}</button>
                <button onClick={handleCancelChange}>{t("common.no")}</button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const meetingsDateBoxStyle = {
    width: "250px",
    fontSize: "1rem",
  };

  const meetingDateSelectStyle = {
    padding: "1rem",
    background: "transparent",
    input: {
      background: "transparent",
    },
  };

  // Dodaj funkcję do wyznaczania minimalnej godziny rozpoczęcia
  const decideMinTimeForStartHour = () => {
    if (!date) {
      const minTime = new Date();
      minTime.setHours(6, 0, 0, 0);
      return minTime;
    }
    if (isSameDay(date, new Date())) {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);
      const minTime = new Date(date);
      minTime.setHours(6, 0, 0, 0);
      // Jeśli teraz jest później niż 6:00, użyj obecnego czasu
      return now > minTime ? now : minTime;
    }
    const minTime = new Date(date);
    minTime.setHours(6, 0, 0, 0);
    return minTime;
  };

  // Funkcja do wyznaczania maksymalnej godziny (18:00)
  const getMaxTime = () => {
    const maxTime = new Date();
    maxTime.setHours(18, 0, 0, 0);
    return maxTime;
  };

  // Generowanie dostępnych godzin od 6:00 do określonej godziny co 15 minut
  const generateAvailableTimes = (baseDate = new Date(), maxHour = 18) => {
    const times = [];
    const startHourValue = 6;
    const endHourValue = maxHour;

    for (let hour = startHourValue; hour <= endHourValue; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === endHourValue && minute > 0) break; // Dla ostatniej godziny tylko pełna godzina
        const time = new Date(baseDate);
        time.setHours(hour, minute, 0, 0);
        times.push(time);
      }
    }
    return times;
  };

  return (
    <div
      className="roomsa-reservations-container"
      // style={{ paddingInline: "20px 20px", height: "100%" }}
    >
      <div className="main-site" style={{ justifyContent: "space-between" }}>
        <div>
          <h1>{t("reservations.rooms.title")}</h1>
          <button
            className="booking-button"
            onClick={() =>
              navigate("/rezerwacje/sale-konferencyjne/zarezerwuj/cyklicznie")
            }
            onMouseEnter={() => setHoveredButton(true)}
            onMouseLeave={() => setHoveredButton(false)}
          >
            <div
              className="icon"
              style={{
                backgroundImage: `url(${
                  hoveredButton ? orangeSortIcon : sortIcon
                })`,
                transform: hoveredButton ? "scale(1.2)" : "rotate(270deg)",
              }}
            ></div>
            {t("reservations.rooms.recurringReservation")}
          </button>
        </div>
        <div
          className="conference-rooms-orange-sort-icon"
          onClick={handleIconClick}
          onMouseEnter={() => setHoveredIcon(true)}
          onMouseLeave={() => setHoveredIcon(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hoveredIcon ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <div className="conference-rooms-main-container">
        <div
          style={{
            width: "100%",
            padding: "0px",
            marginTop: "10px",
          }}
        >
          <div
            className="conference-rooms-date-picker-container"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, max-content)",
              justifyContent: "space-between",
              width: "100%",
              borderBottom: "1px solid orange",
              paddingBottom: "3rem",
              marginBottom: "3rem",
            }}
          >
            <div style={meetingsDateBoxStyle} className="input-group">
              <label htmlFor="date">
                {t("reservations.rooms.meetingDate")}
              </label>
              <DatePicker
                selected={date}
                onChange={handleDateChange}
                dateFormat="dd-MM-yyyy"
                locale={datePickerLocale}
                placeholderText={t("reservations.rooms.selectDate")}
                minDate={new Date()}
                maxDate={addMonths(new Date(), 6)}
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
            <div style={meetingsDateBoxStyle} className="input-group">
              <label htmlFor="startHour">
                {t("reservations.rooms.startTime")}
              </label>
              <DatePicker
                selected={startHour}
                onChange={handleStartHourChange}
                showTimeSelect
                showTimeSelectOnly
                timeCaption={t("reservations.common.time")}
                dateFormat="HH:mm"
                locale={datePickerLocale}
                placeholderText={t("reservations.rooms.selectTime")}
                includeTimes={generateAvailableTimes(
                  date || new Date(),
                  17,
                ).filter((time) => {
                  const isAfterMin = time >= decideMinTimeForStartHour();
                  return isAfterMin;
                })}
                filterTime={(time) => {
                  const hour = time.getHours();
                  return hour >= 6 && hour <= 17;
                }}
                disabled={!date}
              />

              {errors.startHour && (
                <span className="error-message">{errors.startHour}</span>
              )}
            </div>
            <div style={meetingsDateBoxStyle} className="input-group">
              <label htmlFor="endHour">{t("reservations.rooms.endTime")}</label>
              <DatePicker
                selected={endHour}
                onChange={handleEndHourChange}
                showTimeSelect
                showTimeSelectOnly
                timeCaption={t("reservations.common.time")}
                dateFormat="HH:mm"
                locale={datePickerLocale}
                placeholderText={t("reservations.rooms.selectTime")}
                includeTimes={generateAvailableTimes(date || new Date()).filter(
                  (time) => {
                    if (!startHour) return true;
                    // Godzina zakończenia musi być PÓŹNIEJSZA niż rozpoczęcia (nie może być równa)
                    return isAfter(time, startHour);
                  },
                )}
                filterTime={(time) => {
                  const hour = time.getHours();
                  const isInRange = hour >= 6 && hour <= 18;
                  if (!startHour) return isInRange;
                  return isInRange && isAfter(time, startHour);
                }}
                disabled={!date || !startHour}
                style={meetingDateSelectStyle}
              />
              {errors.endHour && (
                <span className="error-message">{errors.endHour}</span>
              )}
            </div>
          </div>
          <div className="conference-rooms-reservation-details-rooms-picker">
            {isDatesSet && (
              <>
                <div className="available-group">
                  <label>{t("reservations.rooms.conferenceRooms")}</label>
                  <div
                    className="cars-list"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, auto)",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    {rooms.map((room) => (
                      <div
                        key={room.id}
                        className={`car-item ${
                          !room.isAvailable ? "disabled" : ""
                        }`}
                        onClick={() =>
                          room.isAvailable && handleCarSelection(room.name)
                        }
                        style={{
                          opacity: room.isAvailable ? 1 : 0.5,
                          cursor: room.isAvailable ? "pointer" : "not-allowed",
                          width: "200px",
                          gap: "15px",
                          display: "flex",
                          alignItems: "center",
                          fontWeight: "bold",
                        }}
                      >
                        <span
                          className={`car-checkbox ${
                            selectedCar === room.name && room.isAvailable
                              ? "checked"
                              : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (room.isAvailable) {
                              handleCarSelection(room.name);
                            }
                          }}
                        />
                        {room.name}
                      </div>
                    ))}
                  </div>
                  {errors.selectedCar && (
                    <span className="error-message">{errors.selectedCar}</span>
                  )}
                </div>
              </>
            )}
          </div>

          <div
            className="conference-rooms-reservation-details-middle-container"
            style={{
              width: "100%",
              marginTop: "3rem",
              display: { isDatesSet } ? "flex" : "none",
              flexDirection: "column",
              gap: "2rem",
            }}
          >
            {isDatesSet && (
              <div className="two-column-layout">
                <div className="left-column">
                  <div className="input-group">
                    <label htmlFor="files">
                      {t("reservations.rooms.attachmentsInfo")}
                    </label>
                    <label htmlFor="files" className="custom-file-upload">
                      {t("reservations.rooms.addFiles")}
                    </label>
                    <input
                      type="file"
                      id="files"
                      multiple
                      onChange={handleFileChange}
                      style={{ display: "none", padding: "12px 40px" }}
                    />
                    {files.length > 0 && (
                      <div className="selected-files">
                        <h4>{t("documentEditor.attachedFiles")}</h4>
                        <ul>
                          {files.map((file, index) => (
                            <li key={index} className="file-item">
                              <span>{file.name}</span>
                              <img
                                src={deleteButtonIcon}
                                alt={t("common.delete")}
                                onClick={() => handleRemoveFile(index)}
                                className="delete-button-icon"
                                style={{
                                  cursor: "pointer",
                                  width: "20px",
                                  height: "20px",
                                  marginLeft: "10px",
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="input-group">
                    <label htmlFor="participants">
                      {t("reservations.rooms.participants")}
                    </label>
                    <Select
                      inputId="newParticipant"
                      options={participantOptions}
                      value={selectedParticipantOption}
                      onChange={(option) =>
                        handleParticipantChange(option?.value ?? "")
                      }
                      placeholder={t("reservations.rooms.selectParticipant")}
                      styles={participantSelectStyles}
                      isOptionDisabled={(option) => option.isBusy}
                      isClearable
                    />
                    <button onClick={handleAddParticipant}>
                      {t("reservations.rooms.addParticipant")}
                    </button>
                    {errors.participants && (
                      <span className="error-down-message">
                        {errors.participants}
                      </span>
                    )}
                    <ul className="participants-list">
                      {participants.map((participant, index) => (
                        <li key={index}>
                          <span>{participant.name}</span>
                          <img
                            src={deleteButtonIcon}
                            alt={t("common.delete")}
                            onClick={() => handleRemoveParticipant(index)}
                            className="delete-button-icon"
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="right-column">
                  <div className="input-group">
                    <label htmlFor="tripPurpose">
                      {t("reservations.rooms.meetingPurpose")}
                    </label>
                    <input
                      type="text"
                      id="tripPurpose"
                      value={tripPurpose}
                      onChange={(e) => setTripPurpose(e.target.value)}
                      placeholder={t("reservations.rooms.enterPurpose")}
                    />
                    {errors.tripPurpose && (
                      <span className="error-message">
                        {errors.tripPurpose}
                      </span>
                    )}
                  </div>

                  <div className="input-group">
                    <label htmlFor="description">
                      {t("reservations.rooms.descriptionInfo")}
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("reservations.rooms.enterDescription")}
                    />
                  </div>
                </div>
              </div>
            )}
            {isDatesSet && (
              <button
                className="reservation-button"
                onClick={handleReservation}
                style={{
                  width: "50%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {t("reservations.rooms.makeReservation")}
              </button>
            )}
          </div>
        </div>
      </div>

      {renderConfirmationModal()}
    </div>
  );
};

export default RoomsReservations;
